import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import ProjectEditor from "@components/projects/project-editor"
import { DictType } from "@pma/types"
import { Separator } from "@components/common/separator"
import { formatDistance } from "date-fns"
import { he } from "date-fns/locale"
import ProjectStatusEditor from "./project-status-editor"
import useTranslator from "@hooks/use-translator"
import DateEditor from "./date-editor"
import { cn } from "@pma/utils/helpers"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import { useAuth } from "@stores/auth-store"
import { ChevronDown, ChevronUp, X } from "lucide-react"
import { toast } from "@hooks/use-toast"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import axios from "axios"
import {
  ALL_ESTIMATE_BOQ_ENDPOINT,
  ALL_MAIN_BOQ_ENDPOINT,
  EDIT_PROJECT_BY_CURRENT_USER_ENDPOINT,
  FINANCIAL_SERVER_URL,
  GET_PROJECT_CUSTOM_COLUMNS_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes"
import { Button } from "@components/common/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import React, { useContext, useEffect, useState } from "react"
import { formatCurrency } from "@pma/utils/utils"
import ProjectWorkTypeEditor from "./project-work-type-editor"
import PmsLoader from "@components/common/loader"
import { Dialog, DialogContent, DialogTitle } from "@components/common/dialog"
import { BoQDetails } from "@components/financials/boq-details"
import { EstimateBoQDetails } from "@components/financials/estimate-boq-details"
import CreateNewBoQEstimate from "@components/financials/create-new-boq-estimate"
import CreateMainBoQ from "@components/financials/create-main-boq"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"
// import ProjectContractorEditor from "./project-contractor-editor"
import { UserRoleContext } from "@pma/contexts/UserRoleContext"
import { Avatar, AvatarImage } from "../common/avatar"
import ProjectContactorCompany from "./Project-contarctor-company"
import ProjectCustomEditor from "./project-custom-editor"

interface Props {
  project: DictType
  refetch: CallableFunction
  sm?: boolean
  isExpanded: boolean
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>
}
const ProjectInfoCard = ({ project, refetch, sm, isExpanded, setExpanded }: Props) => {
  const { translate, locale } = useTranslator()
  const [isLoading, setIsLoading] = useState(false)
  const [openBoQDrawer, setOpenBoQDrawer] = useState(false)
  const [openBoQContext, setOpenBoQContext] = useState("main")
  const [contractorList, setContractorList] = useState<any[]>([])

  const { userRoleDetails } = useContext(UserRoleContext)

  const { authConfig, setTabManager, setProjectFinancial } = useAuth()
  const [searchParam] = useSearchParams()
  const navigate = useNavigate()
  const projectId: any = useParams().projectId

  const isHebrew = locale === "he"
  const members = project?.members_id || []

  const orgId = authConfig?.last_organization_id

  const newParams = new URLSearchParams(searchParam)

  const activeBoQ = searchParam.get("main")

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const getEstimateBoQs = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + ALL_ESTIMATE_BOQ_ENDPOINT,
      {
        organization_id: orgId,
        project_id: projectId,
        show_deleted: false,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: estimateBoQs,
    refetch: refetchEstimates,
    isFetched: isFetchedEstimate,
  } = useQuery({
    queryKey: ["estimate-boq", orgId, project],
    queryFn: getEstimateBoQs,
  })

  const getMainBoQs = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + ALL_MAIN_BOQ_ENDPOINT,
      {
        organization_id: orgId,
        project_id: projectId,
        show_deleted: false,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: mainBoQs,
    isLoading: isLoadingMain,
    refetch: refetchMainBoQs,
    isFetched: isFetchedMain,
  } = useQuery({
    queryKey: ["main-boq", orgId, project],
    queryFn: getMainBoQs,
  })

  const mainEstimate = !isLoading && estimateBoQs?.length > 0 ? estimateBoQs[0] : {}
  const mainBoQFromTender =
    !isLoadingMain && mainBoQs?.length > 0
      ? mainBoQs?.find((boq: DictType) => boq?.estimate_boq_id === mainEstimate?.id) || {}
      : {}

  const setActiveBoQId = (boqId: string) => {
    setTabManager({
      group: "boq",
      main: "main",
      sub: "boq-tender",
      subChild: "boq-items",
    })
    newParams.set("main", boqId)

    !!boqId && navigate(`?${newParams.toString()}`)
  }

  const getActiveBoQ = (boqId: string) => {
    return mainBoQs?.find((boq: DictType) => boq?.id == boqId)
  }

  useEffect(() => {
    if (isFetchedMain && isFetchedEstimate) {
      mainBoQFromTender?.id && setActiveBoQId(mainBoQFromTender?.id || "")

      setProjectFinancial({
        projectId: projectId,
        estimate: mainEstimate?.id,
        main: mainBoQFromTender?.id,
      })
    } else {
      // Reset store financial info
      setProjectFinancial({
        projectId: projectId,
        estimate: undefined,
        main: undefined,
      })
    }
  }, [isFetchedMain, isFetchedEstimate, project])

  const getProjectRoles = async () => {
    const response = await axios.get(SERVER_URL + `/projects/roles/get?project_id=${project.id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const { data: projectUserBaseOnRole, isFetching } = useQuery({
    queryKey: ["user-list", project?.id],
    queryFn: getProjectRoles,
    enabled: project?._can_change_access,
  })

  const getProjectCustomColumns = async () => {
    const response = await axios.get(SERVER_URL + GET_PROJECT_CUSTOM_COLUMNS_ENDPOINT, {
      params: { organization_id: orgId },
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const { data: projectCustomColumns } = useQuery({
    queryKey: ["project-custom-columns", orgId],
    queryFn: getProjectCustomColumns,
    enabled: !!orgId,
  })

  const customColumns = project?.custom_colums || []

  useEffect(() => {
    if (isFetching == false) {
      if (projectUserBaseOnRole?.length) {
        const transformed = projectUserBaseOnRole.map((item: any) => {
          const user = item?._user
          const { _user, ...restRole } = item
          return {
            id: user?.id,
            name: user?.name,
            user_picture: user?.user_picture,
            _role: restRole,
          }
        })

        const combined = [...members, ...transformed]

        const uniqueById = Object.values(
          combined.reduce(
            (acc, item) => {
              acc[item.id] = item // overwrite if duplicate id
              return acc
            },
            {} as Record<number, (typeof members)[0]>,
          ),
        )

        const list: any[] = uniqueById.filter((user: any) => user?._role?.contractor == true)
        setContractorList(list)
      } else {
        const result = members?.filter((item: any) => item?._role?.contractor == true) // 0 - Add, 1 - update
        setContractorList(result)
      }
    }
  }, [isFetching, members])

  return (
    <Card className={cn("min-h-fit", sm ? "h-full w-full md:w-1/2" : "h-full w-full")}>
      <CardHeader className='flex flex-row items-center justify-between'>
        <CardTitle>{translate("Project Information")}</CardTitle>
        <Button
          size={"sm"}
          className='m-0 h-8 w-8 rounded-full p-0'
          style={{ margin: 0 }}
          onClick={() => setExpanded(!isExpanded)}
        >
          {isExpanded ? <ChevronUp /> : <ChevronDown />}
        </Button>
      </CardHeader>

      {isExpanded && (
        <CardContent className='flex min-h-fit flex-col gap-3 text-sm'>
          <div className='flex flex-wrap gap-3'>
            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <CardHeader className='p-2'>
                <ProjectEditor
                  refetch={refetch}
                  id={projectId}
                  title={"title"}
                  value={project?.title}
                  root_folder={project?.root_folder}
                >
                  <CardTitle>{project?.title || "---"}</CardTitle>
                </ProjectEditor>
              </CardHeader>
              <CardContent className='p-2'>
                <p className='text-xs font-normal text-slate-500'>{translate("Project Title")}</p>
              </CardContent>
            </Card>
            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <CardHeader className='p-2'>
                <ProjectEditor
                  refetch={refetch}
                  id={projectId}
                  title={"project_number"}
                  value={project?.project_number}
                >
                  <CardTitle>{project?.project_number || "---"}</CardTitle>
                </ProjectEditor>
              </CardHeader>
              <CardContent className='p-2'>
                <p className='text-xs font-normal text-slate-500'>{translate("Project Number")}</p>
              </CardContent>
            </Card>
            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <CardHeader className='p-2'>
                <CardTitle>{project?.owner?.name || "---"}</CardTitle>
              </CardHeader>
              <CardContent className='p-2'>
                <p className='text-xs font-normal text-slate-500'>{translate("Project Owner")}</p>
              </CardContent>
            </Card>
            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <CardHeader className='p-1.5'>
                <ProjectEditor
                  refetch={refetch}
                  id={projectId}
                  title={"address_text"}
                  value={project?.address_text}
                  type={"address"}
                >
                  <CardTitle className=''>{project?.address_text || "---"}</CardTitle>
                </ProjectEditor>
              </CardHeader>
              <CardContent className='flex items-center p-2'>
                <p className='text-xs font-normal text-slate-500'>{translate("Project Address")}</p>
                {project?.address_text && <DeleteProjectAddress refetch={refetch} id={projectId} />}
              </CardContent>
            </Card>

            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <CardHeader className='p-2'>
                <ProjectContactorCompany project={project} refetch={refetch} />
              </CardHeader>
              <CardContent className='px-2'>
                <p className='text-xs font-normal text-slate-500'>
                  {translate("Contractor Company name")}
                </p>
              </CardContent>
            </Card>
          </div>

          <Separator />

          <h4>{translate("Contractors")}</h4>
          <div className='flex flex-wrap gap-4'>
            {contractorList.map((user: any, index: number) => {
              return (
                <div className='flex gap-1 rounded-full border px-1.5 py-1' key={index}>
                  <Avatar className='h-5 w-5'>
                    <AvatarImage
                      src={user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
                    />
                  </Avatar>
                  <p className='font-medium'>{user?.name}</p>
                </div>
              )
            })}
          </div>

          <Separator />
          <h4>{translate("Timeline")}</h4>
          <div className='flex flex-wrap gap-4'>
            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <DateEditor
                value={{
                  from: project?.start_at,
                  to: project?.finish_to,
                }}
                projectId={projectId}
              />

              <CardContent className='px-2'>
                <p className='text-xs font-normal text-slate-500'>{translate("Duration")}</p>
              </CardContent>
            </Card>

            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <CardHeader className='p-2'>
                <CardTitle>
                  {project?.start_at &&
                    formatDistance(project?.finish_to, project?.start_at, {
                      locale: isHebrew ? he : undefined,
                    })}
                </CardTitle>
              </CardHeader>
              <CardContent className='p-2'>
                <p className='text-xs font-normal text-slate-500'>
                  {translate("Expected finish date")}
                </p>
              </CardContent>
            </Card>

            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <CardHeader className='p-2'>
                <CardTitle>
                  {project?.last_update &&
                    formatDistance(project?.last_update, new Date(), {
                      locale: isHebrew ? he : undefined,
                      addSuffix: true,
                    })}
                </CardTitle>
              </CardHeader>
              <CardContent className='p-2'>
                <p className='text-xs font-normal text-slate-500'>{translate("Last Updated")}</p>
              </CardContent>
            </Card>
          </div>
          <Separator />

          <h4>{translate("Status")}</h4>
          <div className='flex max-w-[500px] flex-wrap justify-between gap-3'>
            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <CardHeader className='p-2'>
                <ProjectStatusEditor status={project?.status} projectId={projectId} />
              </CardHeader>
              <CardContent className='p-2'>
                <p className='text-xs font-normal text-slate-500'>{translate("Status")}</p>
              </CardContent>
            </Card>

            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <CardHeader className='p-2'>
                <ProjectWorkTypeEditor workType={project?.work_type} projectId={projectId} />
              </CardHeader>
              <CardContent className='p-2'>
                <p className='text-xs font-normal text-slate-500'>{translate("Work Type")}</p>
              </CardContent>
            </Card>
            <Card className='flex h-[4.5rem] min-w-[120px] flex-col justify-between bg-slate-200'>
              <CardHeader className='p-1'>
                <ProjectEditor
                  refetch={refetch}
                  id={projectId}
                  title={"neighborhood_id"}
                  value={project?.neighborhood?.id}
                  type={"neighborhood"}
                  setIsLoading={setIsLoading}
                >
                  <CardTitle className='p-1'>
                    {isLoading ? (
                      <PmsLoader className='h-10' />
                    ) : (
                      project?.neighborhood?.name || "---"
                    )}
                  </CardTitle>
                </ProjectEditor>
              </CardHeader>
              <CardContent className='p-1'>
                <p className='text-start text-xs font-normal text-slate-500'>
                  {translate("Neighborhood")}
                </p>
              </CardContent>
            </Card>
          </div>

          <Separator />

          <h4>{translate("Finance")}</h4>
          <div className='flex max-w-[500px] flex-wrap justify-between gap-3'>
            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <CardHeader className='p-2'>
                {estimateBoQs?.length > 0 ? (
                  <Button
                    className='h-7'
                    variant={"outline"}
                    size={"sm"}
                    onClick={() => {
                      setTabManager({
                        group: "boq",
                        main: "estimates",
                        sub: undefined,
                        subChild: undefined,
                      })
                      setOpenBoQContext("estimate")
                      setOpenBoQDrawer(true)
                    }}
                  >
                    <span className='w-[15ch] truncate text-ellipsis'>
                      {translate("Estimate BoQ")}
                    </span>
                  </Button>
                ) : userRoleDetails?.designer ||
                  userRoleDetails?.supervisor ||
                  userRoleDetails.fin_manager ? (
                  <CreateNewBoQEstimate revalidate={refetchEstimates} projectId={projectId} />
                ) : (
                  translate("Create Estimate")
                )}
              </CardHeader>
              <CardContent className=''>
                <CardTitle> {formatCurrency(mainEstimate?.sum || 0, isHebrew)}</CardTitle>
              </CardContent>
            </Card>
            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              {estimateBoQs?.length > 0 ? (
                <CardHeader className='p-2'>
                  {mainBoQs?.length > 0 ? (
                    <Select value={activeBoQ || undefined} onValueChange={setActiveBoQId}>
                      <SelectTrigger
                        className='h-7 w-[160px] rounded-md border-none bg-primary text-white'
                        aria-label='Select a value'
                      >
                        <SelectValue
                          defaultValue={activeBoQ || undefined}
                          placeholder={translate("Main BoQ")}
                        />
                      </SelectTrigger>
                      <SelectContent className='gap-y-2 rounded-xl pb-2'>
                        {mainBoQs.map((boq: DictType, index: number) => (
                          <SelectItem value={String(boq?.id)} className='rounded-lg' key={index}>
                            {translate(boq?.name)}
                          </SelectItem>
                        ))}

                        {userRoleDetails?.supervisor || userRoleDetails?.fin_manager ? (
                          <>
                            <div className='mt-1'>
                              <Separator />
                            </div>
                            <CreateMainBoQ
                              revalidate={refetchMainBoQs}
                              estimateBoQId={mainEstimate?.id}
                              projectId={projectId}
                              asLink
                            />
                          </>
                        ) : null}
                      </SelectContent>
                    </Select>
                  ) : userRoleDetails?.supervisor || userRoleDetails?.fin_manager ? (
                    <CreateMainBoQ
                      revalidate={refetchMainBoQs}
                      estimateBoQId={mainEstimate?.id}
                      projectId={projectId}
                    />
                  ) : (
                    translate("Generate Main BoQ")
                  )}
                </CardHeader>
              ) : <CardHeader className='p-2'>{translate("Main BoQ")}</CardHeader>}

              <CardContent className=''>
                <CardTitle>
                  {activeBoQ && formatCurrency(getActiveBoQ(activeBoQ)?.real_sum || 0, isHebrew)}
                </CardTitle>
              </CardContent>
            </Card>

            <Card className='h-[4.5rem] min-w-[120px] bg-slate-200'>
              <CardHeader className='p-2'>
                {estimateBoQs?.length > 0 && activeBoQ ? (
                  <CardTitle>
                    {" "}
                    {formatCurrency(
                      getActiveBoQ(activeBoQ)?.boq_sum - getActiveBoQ(activeBoQ)?.discount_sum || 0,
                      isHebrew,
                    )}
                    <small className={cn("mx-1")}>{getActiveBoQ(activeBoQ)?.discount} %</small>{" "}
                  </CardTitle>
                ) : (
                  <CardTitle>
                    {formatCurrency(0, isHebrew)}
                    <small className={cn("mx-1")}>0%</small>
                  </CardTitle>
                )}
              </CardHeader>
              <CardContent className='p-2'>
                <p className='text-xs font-normal text-slate-500'>{translate("Discount")}</p>
              </CardContent>
            </Card>
          </div>

          {projectCustomColumns?.length > 0 && (
            <>
              <Separator />
              <h4>{translate("Custom Columns")}</h4>
              <div className='flex max-w-[500px] flex-wrap justify-between gap-3'>
                {projectCustomColumns.map((cColumn: DictType) => {
                  const customValue = customColumns?.find(
                    (column: DictType) => column?.name === cColumn?.name,
                  )

                  return (
                    <Card key={cColumn?.id} className='h-[4.5rem] min-w-[120px] bg-slate-200'>
                      <CardHeader className='p-2'>
                        <ProjectCustomEditor
                          id={projectId}
                          title={cColumn?.name}
                          value={customValue?.value}
                          customId={customValue?.column_id}
                          columnType={cColumn?.type}
                          selectorList={customValue?.list || cColumn?.list}
                          customFile={customValue?._custom_file}
                        >
                          <CardTitle>{customValue?.value || "---"}</CardTitle>
                        </ProjectCustomEditor>
                      </CardHeader>
                      <CardContent className='p-2'>
                        <p className='text-xs font-normal capitalize text-slate-500'>
                          {translate(cColumn?.name)}
                        </p>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </>
          )}
        </CardContent>
      )}

      <Dialog open={openBoQDrawer} onOpenChange={setOpenBoQDrawer}>
        <DialogContent className='h-full w-full overflow-y-auto md:max-w-2xl lg:max-w-4xl xl:max-w-7xl'>
          <div className='flex flex-col py-2'>
            <div className='mb-2 flex gap-2'>
              <DialogTitle className={cn("w-full", isHebrew ? "text-right" : "text-left")}>
                {openBoQContext === "main"
                  ? translate("Bill of Quantities from tender")
                  : translate("Estimate Bill of Quantities")}
              </DialogTitle>
            </div>
            {openBoQContext === "main" && mainBoQs && (
              <BoQDetails
                boqId={mainBoQs[0]?.id}
                closeDialog={() => {
                  refetchMainBoQs()
                  setOpenBoQDrawer(false)
                }}
              />
            )}
            {openBoQContext === "estimate" && estimateBoQs && (
              <EstimateBoQDetails
                boqId={estimateBoQs[0]?.id}
                closeDialog={() => {
                  refetchEstimates()
                  setOpenBoQDrawer(false)
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

export default ProjectInfoCard

const DeleteProjectAddress = ({ id, refetch }: any) => {
  const { authConfig } = useAuth()
  const { translate } = useTranslator()

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const userId = authConfig?.id

  const updateProjectDetails = async () => {
    let payload = {
      project_id: id,
      user_id: userId,
      address_text: "",
      address_point: "",
    }

    const response = await axios.post(
      SERVER_URL + EDIT_PROJECT_BY_CURRENT_USER_ENDPOINT,
      { ...payload },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )

    return response.data
  }

  const updateProjectDetailsMutation = useMutation({
    mutationKey: [`address-text`, id],
    mutationFn: updateProjectDetails,
    onSuccess: (data) => {
      // Revalidate project details
      newParams.set("revalidate", "projects")
      refetch ? refetch("project") : navigate(`?${newParams.toString()}`)
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: `${translate("Error occurred while updating")} ${translate("address")}`,
        variant: "error",
      })
    },
  })

  return (
    <Tooltip>
      <TooltipTrigger className='h-4 w-4 rounded-full p-0.5'>
        <Button size={"icon"} variant={"destructive"} className='h-full w-full rounded-full'>
          <X onClick={() => updateProjectDetailsMutation.mutate()} />
        </Button>
      </TooltipTrigger>
      <TooltipContent className='bg-destructive'>
        {translate("Remove project address")}
      </TooltipContent>
    </Tooltip>
  )
}
